import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useGiftStore } from '../../store/gift-store';
import { ProfileBadge } from '../profile-badge';
import { MoreButton } from '../more-button';
import { ServerSideDatatable } from '../serverside-datatable';
import StringUtils, { truncateString } from '../../utils';
import { GiftFilterer } from './gift-filterer';
import GiftTag from '../GiftTag';

interface Props {}

export const GiftList: React.FC<Props> = () => {
  const { currentPage, loadingGifts, gifts, listGifts, totalCount } =
    useGiftStore();

  const navigate = useNavigate();
  const navigateToGift = useCallback(
    (id: number) => {
      navigate(`/gifts/${id}`);
    },
    [navigate],
  );

  return (
    <ServerSideDatatable
      defaultOrderReference="createdAt"
      searchBarPlaceholder="Search by gift code, buyer email, recipient"
      columns={[
        {
          id: 'giftCode',
          name: <h5 className="text-gray font-medium text-base">Gift Code</h5>,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <Link
              className="text-sm font-medium text-blue hover:underline"
              to={`/gifts/${row.giftId}`}>
              {row.giftCode}
            </Link>
          ),
          grow: 1.5,
          sortable: true,
          sortField: 'giftCode',
        },
        {
          id: 'giverEmail',
          name: <h5 className="text-gray font-medium text-base">Gift Buyer</h5>,
          selector: data => data.giverEmail,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-row hover:underline">
              <div className="flex flex-col justify-center">
                <ProfileBadge
                  firstName={row.giverEmail}
                  lastName={row.giverEmail}
                />
              </div>
              <div className="flex flex-col justify-center px-4 py-[5px]">
                <h6 className="text-black text-sm leading-[19.6px]">
                  {row.giverEmail}
                </h6>
                <p className="text-gray-dark text-xs leading-[18px]">
                  
                </p>
              </div>
            </div>
          ),
          grow: 5,
          sortable: true,
          sortField: 'giverEmail',
        },
        {
          id: 'recipient',
          name: <h5 className="text-gray font-medium text-base">Gift Recipient</h5>,
          selector: data => data.receiverName,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-row hover:underline">
              <div className="flex flex-col justify-center">
                <ProfileBadge
                  firstName={row.receiverName}
                  lastName={row.receiverName}
                />
              </div>
              <div className="flex flex-col justify-center px-4 py-[5px]">
                <h6 className="font-semibold text-black text-sm leading-[19.6px]">
                  {row.receiverName} 
                </h6>
                <p className="text-gray-dark text-xs leading-[18px]">
                  
                </p>
              </div>
            </div>
          ),
          grow: 5,
          sortable: true,
          sortField: 'receiverName',
        },
        {
          id: 'status',
          name: <h5 className="text-gray font-medium text-base">Status</h5>,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => <GiftTag gift={row} />,
          grow: 2,
          sortable: true,
          sortField: 'status',
        },
        {
          id: 'description',
          name: (
            <h5 className="text-gray font-medium text-base">Product</h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <span>
              {/* {truncateString(
                Object.values(row?.name || {}).join(' '),
                20,
              )} */}
              {row.name}
            </span>
          ),
          grow: 4,
        },
        {
          id: 'createdAt',
          name: (
            <h5 className="text-gray font-medium text-base">Date Created</h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-col font-medium text-sm">
              <div>
                {DateTime.fromISO(row.createdAt).toFormat('LL-dd-yyyy')}
              </div>
              <div>{DateTime.fromISO(row.createdAt).toFormat('hh:mm a')}</div>
            </div>
          ),
          grow: 1.75,
          sortable: true,
          sortField: 'createdAt',
        },
        {
          id: 'actions',
          name: '',
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (

            <MoreButton>
              <button
                type="button"
                className="text-base hover:bg-blue-lighter hover:text-blue text-black font-medium w-full px-4 py-1 text-left"
                onClick={() => navigateToGift(row.giftId)}>
                View Gift
              </button>
            </MoreButton>
          ),
          grow: 1,
        },
      ]}
      data={gifts}
      currentPage={currentPage}
      paginationTotalRows={totalCount}
      progressPending={loadingGifts}
      serverAction={listGifts}
      filterComponent={<GiftFilterer />}
    />
  );
};
